"use client";

import { I18nextProvider } from "react-i18next";
import i18n, { SUPPORTED_LANGUAGES } from "@/lib/i18n/config";
import { useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";

function isSupportedLang(lang: string): boolean {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

interface I18nProviderProps {
  children: React.ReactNode;
  lang?: string;
}

function I18nProviderInner({ children, lang }: I18nProviderProps) {
  const searchParams = useSearchParams();


  // CRITICAL FIX: On server, always ensure we start with the correct language
  // This prevents server-side language persistence across requests
  // The singleton i18n instance can retain state from previous requests
  if (typeof window === "undefined") {
    // Server-side: Always set to the intended language
    const targetLang = lang || "en";
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  } else if (!lang && i18n.language !== "en") {
    // Client-side: Reset to English if no lang prop (for consistency with server)
    i18n.changeLanguage("en");
  }

  // Set language synchronously from prop during render (both server and client).
  // This ensures server HTML and client hydration produce identical output.
  // Resources are bundled so changeLanguage is effectively synchronous.
  if (
    lang &&
    isSupportedLang(lang) &&
    i18n.language !== lang
  ) {
    i18n.changeLanguage(lang);
  }

  // Client-only: handle navigation changes and fallback detection when no lang prop.
  // On pages with ?lang=, this is usually a no-op since the prop already set the language.
  // On pages without ?lang= (entry page), this detects from browser/localStorage.
  useEffect(() => {
    const urlLang = searchParams?.get("lang");

    // Priority 1: URL parameter
    if (urlLang && isSupportedLang(urlLang)) {
      if (i18n.language !== urlLang) {
        i18n.changeLanguage(urlLang);
      }
      localStorage.setItem("i18nextLng", urlLang);
      return;
    }

    // If lang prop already set the language, no need for fallback detection
    if (lang && isSupportedLang(lang)) {
      return;
    }

    // Priority 2: Browser language (only when no URL param and no prop)
    if (typeof navigator !== "undefined") {
      const browserLang =
        navigator.language || (navigator as any).userLanguage;
      const baseLang = browserLang.split("-")[0].toLowerCase();
      if (isSupportedLang(baseLang)) {
        if (i18n.language !== baseLang) {
          i18n.changeLanguage(baseLang);
        }
        return;
      }
    }

    // Priority 3: localStorage
    const storedLang = localStorage.getItem("i18nextLng");
    if (storedLang && isSupportedLang(storedLang)) {
      if (i18n.language !== storedLang) {
        i18n.changeLanguage(storedLang);
      }
      return;
    }
  }, [searchParams, lang]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

export function I18nProvider({ children, lang }: I18nProviderProps) {
  return (
    <Suspense fallback={children}>
      <I18nProviderInner lang={lang}>{children}</I18nProviderInner>
    </Suspense>
  );
}
